import { useEffect, useState } from 'react';

export type Checkbox = {
  label: string;
  value: string;
  checked?: boolean;
  visible?: boolean;
  error?: string;
};

type Args = { availableStates: Checkbox[] };

const useAssignStates = ({ availableStates }: Args) => {
  const [available, setAvailable] = useState<Checkbox[]>([]);
  const [assigned, setAssigned] = useState<Checkbox[]>([]);

  const [search, setSearch] = useState({
    available: '',
    assigned: '',
  });

  useEffect(() => {
    const availableInitial = availableStates
      .filter((item) => !item.checked)
      .map((item) => ({
        ...item,
        checked: false,
        visible: true,
      }));

    const assignedInitial = availableStates
      .filter((item) => item.checked)
      .map((item) => ({
        ...item,
        visible: true,
      }));

    setAvailable(availableInitial);
    setAssigned(assignedInitial);
  }, [availableStates]);

  useEffect(() => {
    setAvailable((prev) => prev.map((item) => ({ ...item, error: '' })));
  }, [available.length]);

  const assignAll = () => {
    setAssigned((prev) =>
      [...prev, ...available.filter((item) => item.visible)].map((item) => ({
        ...item,
        checked: false,
      })),
    );
    setAvailable((prev) => prev.filter((item) => item.visible === false));
  };

  const unassignAll = () => {
    setAvailable((prev) =>
      [...prev, ...assigned.filter((item) => item.visible)].map((item) => ({
        ...item,
        checked: false,
      })),
    );
    setAssigned((prev) => prev.filter((item) => item.visible === false));
  };

  const assignSelected = () => {
    setAssigned((prev) => [
      ...prev,
      ...available
        .filter((item) => item.checked && item.visible)
        .map((item) => ({ ...item, checked: false })),
    ]);
    setAvailable((prev) => prev.filter((item) => item.checked === false));
  };

  const unassignSelected = () => {
    setAvailable((prev) => [
      ...prev,
      ...assigned
        .filter((item) => item.checked && item.visible)
        .map((item) => ({ ...item, checked: false })),
    ]);
    setAssigned((prev) => prev.filter((item) => item.checked === false));
  };

  const selectAvailableItem = (name: string, checked?: boolean) => {
    setAvailable((prev) =>
      prev.map((item) => (item.label === name ? { ...item, checked } : item)),
    );
  };

  const selectAssignedItem = (name: string, checked?: boolean) => {
    setAssigned((prev) =>
      prev.map((item) => (item.label === name ? { ...item, checked } : item)),
    );
  };

  const onSearch = (value: string, level: 'available' | 'assigned') => {
    setSearch((prev) => ({ ...prev, [level]: value }));

    const filter = (items: Checkbox[]) =>
      items.map((item) =>
        new RegExp(value, 'ig').test(item.label)
          ? { ...item, visible: true }
          : { ...item, visible: false },
      );

    if (level === 'available') {
      setAvailable(filter);
    } else {
      setAssigned(filter);
    }
  };

  return {
    available,
    assigned,
    search,
    onSearch,
    assignAll,
    unassignAll,
    setAssigned,
    assignSelected,
    unassignSelected,
    selectAvailableItem,
    selectAssignedItem,
  };
};

export default useAssignStates;
